import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ApiErrorAlert from 'src/components/_common/ApiErrorAlert/ApiErrorAlert';
import Button from 'src/components/_common/Button/Button';
import useAddAttendanceLogEntry from 'src/hooks/attendance/useAddAttendanceLogEntry';
import useAttendanceLog from 'src/hooks/attendance/useAttendanceLog';
import useUpdateAttendanceLogEntry from 'src/hooks/attendance/useUpdateAttendanceLogEntry';
import ToastsContext from 'src/hooks/useToasts';
import AttendanceLogEntry from 'src/models/AttendanceLogEntry';

interface AttendanceLogProps {
  classId: number;
  studentUserId: number;
  attendanceId: number;
  loggedInUserId?: number;
}

const textAreaClassName = 'w-full border rounded px-2 py-1';

export default function AttendanceLog({ classId, studentUserId, attendanceId, loggedInUserId }: AttendanceLogProps) {
  const { formatMessage } = useIntl();
  const logQuery = useAttendanceLog(classId, studentUserId);
  const addMutation = useAddAttendanceLogEntry();
  const updateMutation = useUpdateAttendanceLogEntry();
  const { setShowSuccessToast } = ToastsContext.useContainer();
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  if (logQuery.isLoading) return null;
  if (logQuery.isError) {
    return <ApiErrorAlert error={logQuery.error} message={formatMessage({ id: 'attendance-log-load-error' })} />;
  }

  const entries = logQuery.data.filter(entry => entry.attendanceId === attendanceId);
  const mutationError = addMutation.error ?? updateMutation.error;

  function handleAdd() {
    addMutation.mutate(
      { classId, studentUserId, attendanceId, text: newText },
      {
        onSuccess: () => {
          setNewText('');
          setShowSuccessToast(true);
        },
      },
    );
  }

  function startEdit(entry: AttendanceLogEntry) {
    setEditingId(entry.id);
    setEditText(entry.text);
  }

  function handleUpdate(logEntryId: number) {
    updateMutation.mutate(
      { classId, studentUserId, attendanceId, logEntryId, text: editText },
      {
        onSuccess: () => {
          setEditingId(null);
          setShowSuccessToast(true);
        },
      },
    );
  }

  return (
    <div className="py-2" data-testid={`attendance-log-${attendanceId}`}>
      <p className="text-sm text-customGray mb-2">
        <FormattedMessage id="attendance-log-visibility" />
      </p>
      {mutationError && (
        <ApiErrorAlert error={mutationError} message={formatMessage({ id: 'attendance-log-save-error' })} />
      )}
      {entries.length === 0 && (
        <p className="mb-2"><FormattedMessage id="attendance-log-empty" /></p>
      )}
      <ul>
        {entries.map(entry => (
          <li key={entry.id} className="mb-3 border-l-4 pl-3">
            <div className="text-sm text-customGray">
              {new Date(entry.createdAt).toLocaleDateString().replace(/\//g, '.')}
            </div>
            {editingId === entry.id ? (
              <>
                <textarea
                  className={textAreaClassName}
                  rows={3}
                  value={editText}
                  aria-label={formatMessage({ id: 'attendance-log-edit-aria' })}
                  onChange={e => setEditText(e.target.value)}
                />
                <div className="flex justify-end gap-2 mt-1">
                  <Button size="small" layout="outline" onClick={() => setEditingId(null)}>
                    <FormattedMessage id="cancel" />
                  </Button>
                  <Button
                    size="small"
                    isSaving={updateMutation.isLoading}
                    disabled={!editText.trim()}
                    onClick={() => handleUpdate(entry.id)}
                  />
                </div>
              </>
            ) : (
              <div className="flex justify-between items-start gap-2">
                <p className="whitespace-pre-wrap">{entry.text}</p>
                {entry.userId === loggedInUserId && (
                  <Button size="small" layout="link" onClick={() => startEdit(entry)}>
                    <FormattedMessage id="edit" />
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      <textarea
        className={textAreaClassName}
        rows={3}
        value={newText}
        placeholder={formatMessage({ id: 'attendance-log-new-placeholder' })}
        aria-label={formatMessage({ id: 'attendance-log-new-placeholder' })}
        onChange={e => setNewText(e.target.value)}
      />
      <div className="flex justify-end mt-1">
        <Button size="small" isSaving={addMutation.isLoading} disabled={!newText.trim()} onClick={handleAdd}>
          <FormattedMessage id="attendance-log-add" />
        </Button>
      </div>
    </div>
  );
}
